import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  // 创建商品唯一标识符的函数（与CartContext中的逻辑一致）
  const createItemKey = (item) => {
    const options = [];
    
    // 添加颜色选项
    if (item.color) options.push(`color-${item.color}`);
    if (item.selectedColor) options.push(`selectedColor-${item.selectedColor}`);
    
    // 添加其他选项
    if (item.connectivity) options.push(`connectivity-${item.connectivity}`);
    if (item.dpi) options.push(`dpi-${item.dpi}`);
    if (item.length) options.push(`length-${item.length}`);
    if (item.selectedLength) options.push(`selectedLength-${item.selectedLength}`);
    if (item.selectedSize) options.push(`selectedSize-${item.selectedSize}`);
    if (item.selectedPlug) options.push(`selectedPlug-${item.selectedPlug}`);
    if (item.version) options.push(`version-${item.version}`);
    
    // 添加Switch 2 Protect Screen特定选项
    if (item.selectedSurface) options.push(`surface-${item.selectedSurface.id}`);
    if (item.selectedAddOn) options.push(`addon-${item.selectedAddOn.id}`);
    
    // 添加其他add-on选项
    if (item.wristStrap) options.push('wristStrap');
    if (item.selectedEssentialOil) options.push(`essentialOil-${item.selectedEssentialOil}`);
    if (item.selectedAddOns) options.push(`addons-${item.selectedAddOns.join('-')}`);
    
    const optionsString = options.length > 0 ? `-${options.join('-')}` : '';
    return `${item.id}${optionsString}`;
  };

  const formatPrice = (price) => {
    // 如果价格是null或undefined，返回0
    if (price === null || price === undefined) {
      return 'S$0.00';
    }
    
    // 如果价格已经是格式化的字符串（如 "S$22.90"），直接返回
    if (typeof price === 'string') {
      return price;
    }
    
    // 如果价格是数字，格式化为 "S$" 格式
    if (typeof price === 'number' && !isNaN(price)) {
      return `S$${price.toFixed(2)}`;
    }
    
    // 如果价格是其他格式，尝试转换为数字
    const numericPrice = parseFloat(price);
    if (!isNaN(numericPrice)) {
      return `S$${numericPrice.toFixed(2)}`;
    }
    
    // 如果都无法处理，返回0
    return 'S$0.00';
  };

  // 计算单个商品的总价（包含 add-on）
  const getItemTotalPrice = (item) => {
    if (!item || !item.price) return 0;
    
    // 处理价格格式 - 确保是字符串
    let priceStr = typeof item.price === 'string' ? item.price : item.price.toString();
    let total = parseFloat(priceStr.replace('S$', '')) * item.quantity;
    
    // 添加腕带价格
    if (item.wristStrap) {
      total += 9.90 * item.quantity;
    }
    
    // 添加 add-on 价格
    if (item.selectedAddOns && Array.isArray(item.selectedAddOns) && item.selectedAddOns.length > 0) {
      item.selectedAddOns.forEach(addOn => {
        if (addOn && addOn.price && typeof addOn.price === 'string') {
          const addOnPrice = parseFloat(addOn.price.replace('S$', ''));
          if (!isNaN(addOnPrice)) {
            total += addOnPrice * item.quantity;
          }
        }
      });
    }
    
    return total;
  };

  // 计算单个商品的总价（包括附加项目）
  const calculateItemTotal = (item) => {
    // 确保价格是数字
    let basePrice = 0;
    if (typeof item.price === 'string') {
      // 如果是字符串格式的价格（如"S$22.90"），提取数字部分
      const priceMatch = item.price.match(/[\d.]+/);
      basePrice = priceMatch ? parseFloat(priceMatch[0]) : 0;
    } else if (typeof item.price === 'number') {
      basePrice = item.price;
    } else {
      basePrice = 0;
    }
    
    let addOnPrice = 0;
    
    // 计算附加项目价格
    if (item.wristStrap) {
      addOnPrice += 9.90; // Wrist Strap 价格
    }
    
    // 添加 selectedAddOns 价格
    if (item.selectedAddOns && Array.isArray(item.selectedAddOns) && item.selectedAddOns.length > 0) {
      item.selectedAddOns.forEach(addOn => {
        if (addOn && addOn.price && typeof addOn.price === 'string') {
          const addOnPriceValue = parseFloat(addOn.price.replace('S$', ''));
          if (!isNaN(addOnPriceValue)) {
            addOnPrice += addOnPriceValue;
          }
        }
      });
    }
    
    // 计算总价：基础价格 + 附加项目价格，然后乘以数量
    return (basePrice + addOnPrice) * item.quantity;
  };

  // 计算购物车总价（包括所有附加项目）
  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + calculateItemTotal(item);
    }, 0);
  };

  const shippingCost = 0; // Free meetup/self-collection
  const totalAmount = calculateCartTotal() + shippingCost;

  // Generate WhatsApp message with cart items
  const generateWhatsAppMessage = () => {
    const items = cart.map(item => {
      let itemText = `${item.name} x${item.quantity}`;
      
      // Add version information
      if (item.version) {
        itemText += ` (${item.version})`;
      }
      
      // Add add-ons information with prices
      const addOns = [];
      if (item.wristStrap) addOns.push('Wrist Strap (+S$9.90)');
      if (item.selectedAddOns && item.selectedAddOns.length > 0) {
        item.selectedAddOns.forEach(addOn => {
          addOns.push(`${addOn.name} (${addOn.price})`);
        });
      }
      if (item.color) addOns.push(`Color: ${item.color}`);
      if (item.dpi) addOns.push(`DPI: ${item.dpi}`);
      if (item.connectivity) addOns.push(`Connectivity: ${item.connectivity}`);
      if (item.length) addOns.push(`Length: ${item.length}`);
      
      if (addOns.length > 0) {
        itemText += ` [${addOns.join(', ')}]`;
      }
      
      return itemText;
    }).join(', ');
    
    const total = formatPrice(totalAmount);
    return `Hi! I want to self-collect ${items}. Total: ${total}`;
  };

  // Handle WhatsApp checkout
  const handleWhatsAppCheckout = () => {
    const message = encodeURIComponent(generateWhatsAppMessage());
    const whatsappUrl = `https://wa.me/6590514719?text=${message}`;
    window.open(whatsappUrl, '_blank');
    setShowCheckoutModal(false);
  };

  // Handle delivery form checkout
  const handleDeliveryCheckout = () => {
    window.open('https://forms.gle/iNVb3cCXvvzw6cx67', '_blank');
    setShowCheckoutModal(false);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-dark-400 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-8xl mb-6">🛒</div>
            <h2 className="text-3xl font-bold text-white mb-4">Your Cart is Empty</h2>
            <p className="text-xl text-gray-400 mb-8">
              Explore our RGB-enhanced gaming accessories to start your shopping journey
            </p>
            <Link to="/shop">
              <motion.button
                className="btn-primary text-lg px-8 py-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Shopping
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-futuristic font-bold mb-4 text-white">
            SHOPPING CART
          </h1>
          <p className="text-xl text-gray-400">
            {cart.length} items in your cart
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <motion.div
              className="flex justify-between items-center mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-2xl font-semibold text-white">Items</h2>
              <button
                onClick={clearCart}
                className="text-red-400 hover:text-red-300 transition-colors text-sm"
              >
                Clear Cart
              </button>
            </motion.div>

            <AnimatePresence>
              {cart.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="card-glass p-6 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20, height: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  layout
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <Link
                        to={`/product/${item.id}`}
                        className="text-lg font-semibold text-white hover:text-primary-400 transition-colors"
                      >
                        {item.name}
                      </Link>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                        {item.description}
                      </p>
                      
                      {/* Version and Add-ons Information */}
                      <div className="mt-2 space-y-1 border border-gray-600 p-2 rounded bg-gray-800/50">
                        
                        {/* Version Selection */}
                        {item.version && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Version:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.version}
                            </span>
                          </div>
                        )}
                        
                        {/* Add-ons */}
                        {item.wristStrap && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Add-on:</span>
                            <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                              ✓ Wrist Strap (+S$9.90)
                            </span>
                          </div>
                        )}
                        
                        {/* Selected Add-ons */}
                        {item.selectedAddOns && item.selectedAddOns.length > 0 && (
                          <div className="space-y-1">
                            {item.selectedAddOns.map((addOn, addOnIndex) => (
                              <div key={addOnIndex} className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Add-on:</span>
                                <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                                  ✓ {addOn.name} (+{addOn.price})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        
                        {/* Other add-ons can be added here */}
                        {item.color && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Color:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.color}
                            </span>
                          </div>
                        )}
                        
                        {item.selectedEssentialOil && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Essential Oil:</span>
                            <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                              ✓ {item.selectedEssentialOil} (+S$2.90)
                            </span>
                          </div>
                        )}
                        
                        {item.dpi && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">DPI:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.dpi}
                            </span>
                          </div>
                        )}
                        
                        {item.connectivity && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Connectivity:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.connectivity}
                            </span>
                          </div>
                        )}
                        
                        {item.length && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Length:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.length}
                            </span>
                          </div>
                        )}
                        
                        {/* Show selected options for LED products */}
                        {(item.selectedColor || item.selectedLength) && (
                          <div className="space-y-1">
                            {item.selectedColor && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Color:</span>
                                <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                                  {item.selectedColor}
                                </span>
                              </div>
                            )}
                            {item.selectedLength && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Length:</span>
                                <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                                  {item.selectedLength}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show selected size for RGB Gaming Mouse Pad */}
                        {item.selectedSize && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">Size:</span>
                              <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                                {item.selectedSize}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Show selected plug type for Switch AC Adapter */}
                        {item.selectedPlug && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">Plug:</span>
                              <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                                {item.selectedPlug}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Switch 2 Protect Screen specific options */}
                        {item.selectedSurface && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Surface:</span>
                            <span className="text-xs text-white bg-gray-700 px-2 py-1 rounded">
                              {item.selectedSurface.name}
                            </span>
                          </div>
                        )}
                        
                        {item.selectedAddOn && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Add-on:</span>
                            <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                              ✓ {item.selectedAddOn.name} (+{item.selectedAddOn.price})
                            </span>
                          </div>
                        )}

                        {/* Show message if no options selected */}
                        {!item.version && !item.wristStrap && !item.color && !item.selectedEssentialOil && !item.dpi && !item.connectivity && !item.length && !item.selectedColor && !item.selectedLength && !item.selectedSize && !item.selectedPlug && !item.selectedSurface && !item.selectedAddOn && !item.selectedAddOns && (
                          <div className="text-xs text-gray-400 italic">
                            No custom options selected
                          </div>
                        )}
                        
                        {/* Select Options Button */}
                        <div className="mt-2">
                          <Link
                            to={`/product/${item.id}`}
                            className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Select Options
                          </Link>
                        </div>
                      </div>
                      
                                             {/* Price */}
                       <div className="flex items-center space-x-2 mt-2">
                         <span className="text-xl font-bold text-white">
                           {formatPrice(getItemTotalPrice(item))}
                         </span>
                         {item.originalPrice && (() => {
                           // 提取原始价格的数字部分进行比较
                           const currentPriceMatch = item.price.match(/[\d.]+/);
                           const originalPriceMatch = item.originalPrice.match(/[\d.]+/);
                           const currentPrice = currentPriceMatch ? parseFloat(currentPriceMatch[0]) : 0;
                           const originalPrice = originalPriceMatch ? parseFloat(originalPriceMatch[0]) : 0;
                           
                           if (originalPrice > currentPrice) {
                             return (
                               <span className="text-sm text-gray-500 line-through">
                                 {formatPrice(item.originalPrice)}
                               </span>
                             );
                           }
                           return null;
                         })()}
                         
                       </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-white text-sm">Qty:</span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              const itemKey = createItemKey(item);
                              updateQuantity(itemKey, item.quantity - 1);
                            }}
                            className="w-8 h-8 bg-dark-300 border border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="w-8 text-center text-white font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => {
                              const itemKey = createItemKey(item);
                              updateQuantity(itemKey, item.quantity + 1);
                            }}
                            className="w-8 h-8 bg-dark-300 border border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>

                                             {/* Subtotal */}
                       <div className="text-right">
                         <div className="text-lg font-bold text-white">
                           {formatPrice(calculateItemTotal(item))}
                         </div>
                         <button
                           onClick={() => {
                             const itemKey = createItemKey(item);
                             removeFromCart(itemKey);
                           }}
                           className="text-red-400 hover:text-red-300 transition-colors text-sm mt-1"
                         >
                           Remove
                         </button>
                       </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Continue Shopping */}
            <motion.div
              className="pt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/shop">
                <button className="btn-outline">
                  ← Continue Shopping
                </button>
              </Link>
            </motion.div>
          </div>

          {/* Order Summary */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card-glass p-6 rounded-lg sticky top-24">
              <h3 className="text-xl font-semibold text-white mb-6">Order Summary</h3>
              
              <div className="space-y-4">
                {/* Subtotal */}
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal:</span>
                  <span>{formatPrice(calculateCartTotal())}</span>
                </div>

                {/* Shipping */}
                <div className="text-gray-300">
                  <div className="flex justify-between items-start">
                    <span>Shipping:</span>
                    <span className="text-green-400">FREE</span>
                  </div>
                  <div className="text-left mt-1">
                    <span className="text-gray-400 text-sm">(Meet Up / Self-collection)</span>
                  </div>
                </div>

                {/* Shipping Notice */}
                <div className="text-sm text-green-400 bg-green-400/10 p-3 rounded-lg">
                  ✓ Free meetup/self-collection available
                </div>

                {/* Divider */}
                <div className="border-t border-white/20 my-4"></div>

                {/* Total */}
                <div className="flex justify-between text-xl font-bold text-white">
                  <span>Total:</span>
                  <span>{formatPrice(totalAmount)}</span>
                </div>

                {/* Tax Info */}
                <p className="text-xs text-gray-400 text-center">
                  Tax included
                </p>

                {/* Checkout Button */}
                <button 
                  onClick={() => setShowCheckoutModal(true)}
                  className="btn-primary w-full text-lg py-4 mt-6"
                >
                  Checkout
                </button>

                {/* Security Badge */}
                <div className="flex items-center justify-center space-x-2 mt-4 text-sm text-gray-400">
                  <span>🔒</span>
                  <span>Secure Payment</span>
                </div>

                {/* Payment Methods */}
                <div className="mt-6 space-y-3">
                  <p className="text-sm text-gray-400 text-center">Accepted Payment Methods</p>
                  <div className="flex justify-center space-x-4">
                    {['💳', '🏦', '📱', '💰'].map((icon, index) => (
                      <div
                        key={index}
                        className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center text-xl"
                      >
                        {icon}
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Assistance */}
                <div className="mt-6 p-4 bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-lg border border-primary-500/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                    <span className="text-sm text-primary-400 font-medium">AI Assistant</span>
                  </div>
                  <p className="text-xs text-gray-300">
                    Our AI system is optimizing delivery routes for fastest shipping.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckoutModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="card-glass p-8 rounded-2xl max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Choose Checkout Method</h3>
                <p className="text-gray-400">Select how you'd like to complete your order</p>
              </div>

              <div className="space-y-4">
                {/* Self-collection Option */}
                <button
                  onClick={handleWhatsAppCheckout}
                  className="w-full p-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-3"
                >
                  <span className="text-2xl">📱</span>
                  <span>Self-collection / Meetup</span>
                </button>

                {/* Delivery Option */}
                <button
                  onClick={handleDeliveryCheckout}
                  className="w-full p-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-3"
                >
                  <span className="text-2xl">🚚</span>
                  <span>Delivery</span>
                </button>

                {/* Cancel Button */}
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="w-full p-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>

              <div className="mt-6 p-4 bg-gradient-to-r from-yellow-500/10 to-yellow-600/10 rounded-lg border border-yellow-500/20">
                <p className="text-xs text-yellow-400 text-center">
                  💬 After submitting, we will contact you via WhatsApp to confirm your order and payment.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cart;
